// react_ujs mounts every `[data-react-class]` root under a selector in one synchronous
// loop, so module evaluation, props JSON.parse and hydrateRoot for all roots land in a
// single task and a tap in that window waits for all of it. This mounts one root per
// task instead, and leaves roots far from the viewport until they come within
// `rootMargin` or the main thread goes idle.

const MOUNT_SLOT_ATTR = 'data-react-mount-slot'

export function yieldToMain() {
  if (typeof window.scheduler?.yield === 'function') {
    return window.scheduler.yield()
  }

  return new Promise((resolve) => { setTimeout(resolve, 0) })
}

// `mountComponents(selector)` matches the root itself (`selector[data-react-class]`),
// whereas `mountComponents(node)` only searches the node's descendants.
function mountRoot(ReactRailsUJS, node, slot) {
  node.setAttribute(MOUNT_SLOT_ATTR, slot)
  ReactRailsUJS.mountComponents(`[${MOUNT_SLOT_ATTR}="${slot}"]`)
}

function reportMountError(error, node) {
  if (typeof window.sendErrorToSentry === 'function') {
    window.sendErrorToSentry(error, {
      tags: { section: 'mount-roots-incrementally' },
      extra: { component: node.getAttribute('data-react-class') },
    })
  } else {
    console.error('[react-rails] Failed to mount root', error)
  }
}

function mountSequentially(ReactRailsUJS, entries) {
  return entries.reduce(
    (previous, entry) => previous
      .then(yieldToMain)
      .then(() => {
        try {
          mountRoot(ReactRailsUJS, entry.node, entry.slot)
        } catch (error) {
          reportMountError(error, entry.node)
        }
      }),
    Promise.resolve()
  )
}

function isNearViewport(node, viewportHeight) {
  const { top, bottom } = node.getBoundingClientRect()

  return bottom > -viewportHeight / 2 && top < viewportHeight * 1.5
}

// `nearDone` keeps the roots around the viewport ahead of these even when the idle
// callback or an intersection fires while they are still mounting.
function mountWhenVisible(ReactRailsUJS, entries, nearDone, { rootMargin, idleTimeout }) {
  const pending = new Map(entries.map((entry) => [entry.node, entry]))

  const takePending = (nodes) => nodes
    .filter((node) => pending.has(node))
    .map((node) => {
      const entry = pending.get(node)
      pending.delete(node)
      return entry
    })

  const mountAfterNear = (nodes) => {
    const taken = takePending(nodes)

    return nearDone.then(() => mountSequentially(ReactRailsUJS, taken))
  }

  if (!('IntersectionObserver' in window)) {
    mountAfterNear(Array.from(pending.keys()))
    return
  }

  const observer = new IntersectionObserver((observed) => {
    const visible = observed.filter((item) => item.isIntersecting).map((item) => item.target)

    visible.forEach((node) => observer.unobserve(node))
    mountAfterNear(visible)
  }, { rootMargin })

  pending.forEach((entry, node) => observer.observe(node))

  // Deep links and no-scroll sessions still get an interactive page: hydrate whatever is
  // left once the main thread is idle, or at the latest after `idleTimeout`.
  const mountRest = () => {
    observer.disconnect()
    mountAfterNear(Array.from(pending.keys()))
  }

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(mountRest, { timeout: idleTimeout })
  } else {
    setTimeout(mountRest, idleTimeout)
  }
}

export default function mountRootsIncrementally(
  ReactRailsUJS,
  container,
  { rootMargin = '600px', idleTimeout = 10000 } = {}
) {
  if (!container) {
    return Promise.resolve()
  }

  const viewportHeight = window.innerHeight
  const entries = Array.from(container.querySelectorAll(`[${ReactRailsUJS.CLASS_NAME_ATTR}]`))
    .map((node, index) => ({ node, slot: String(index) }))
  const near = entries.filter((entry) => isNearViewport(entry.node, viewportHeight))
  const far = entries.filter((entry) => !near.includes(entry))
  const nearDone = mountSequentially(ReactRailsUJS, near)

  mountWhenVisible(ReactRailsUJS, far, nearDone, { rootMargin, idleTimeout })

  return nearDone
}
